import React, { useContext, useMemo, useState } from 'react';

import {
  EditorContext,
  PluginsContext,
  RicosContext,
  ToolbarContext,
  useTrackRender,
} from './../../../ricos-context';
import type { RicosPluginToolbar } from './../../../ricos-plugins/pluginToolbar';
import { PluginNameToToolbarIdMap } from './../../../ricos-types';
import { useToolbarBoundary } from './../../toolbar-boundary';

import { FloatingToolbar } from '../../components/FloatingToolbar/FloatingToolbar';
import { MaxWidthCalculationWrapper } from '../../components/MaxWidthCalculationWrapper/MaxWidthCalculationWrapper';
import { Toolbar } from '../../components/Toolbar';
import { FLOATING_PLUGIN_TOOLBAR } from './data-hooks';
import { getReferenceElement } from './get-plugin-reference-element';
import styles from './PluginToolbar-scss';

const PluginsToolbarContent = ({
  toolbar,
  boundary,
}: {
  toolbar: RicosPluginToolbar;
  boundary: HTMLElement;
}) => {
  useTrackRender('Plugin toolbar');

  const { languageDir, isMobile } = useContext(RicosContext);
  const { toolbars } = useContext(ToolbarContext);

  const [maxWidth, setMaxWidth] = useState(() => boundary?.clientWidth ?? 0);
  const itemsConfigFilter = toolbars.plugin.getSettings()?.filterItems;
  const itemsConfig = useMemo(() => toolbar.toToolbarItemsConfig(), [toolbar]);
  const filteredItemsConfig = useMemo(
    () =>
      itemsConfigFilter
        ? itemsConfig.filter(item =>
            itemsConfigFilter(item, PluginNameToToolbarIdMap[toolbar.pluginType])
          )
        : itemsConfig,
    [itemsConfig, itemsConfigFilter]
  );

  const itemsRenderers = useMemo(() => toolbar.getToolbarButtonsRenderers(), [toolbar]);

  const dataHook = `${FLOATING_PLUGIN_TOOLBAR} ${toolbar.pluginType ?? ''}`;

  return (
    <MaxWidthCalculationWrapper
      dir={languageDir}
      data-hook={dataHook}
      className={styles.floatingToolbar}
      setMaxWidth={setMaxWidth}
      otherWrapper={boundary}
    >
      <Toolbar
        type="floating"
        isMobile={isMobile}
        toolbarItemsConfig={filteredItemsConfig}
        toolbarItemsRenderers={itemsRenderers}
        maxWidth={maxWidth}
      />
    </MaxWidthCalculationWrapper>
  );
};

export const PluginsToolbar = () => {
  const plugins = useContext(PluginsContext);
  const { toolbars } = useContext(ToolbarContext);
  const ricosEditor = useContext(EditorContext);
  const boundary = useToolbarBoundary();
  const {
    adapter: { tiptapEditor },
  } = ricosEditor;

  const shownToolbar = plugins.getVisibleToolbar(tiptapEditor.state.selection, ricosEditor);

  if (!shownToolbar || !boundary || toolbars.formatting.isVisible() || toolbars.plugin.isEmpty()) {
    return null;
  }

  const referenceElement = getReferenceElement(shownToolbar, tiptapEditor);

  if (!referenceElement) {
    return null;
  }

  return (
    <FloatingToolbar
      hideStrategy="escaped"
      boundary={boundary}
      referenceElement={referenceElement}
      placement="top"
    >
      <PluginsToolbarContent toolbar={shownToolbar} boundary={boundary} />
    </FloatingToolbar>
  );
};
